import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, GraduationCap, FileCheck2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ROLES, formatDate } from "@/lib/constants";
import { useOrg } from "@/hooks/useOrg";
import { PageHeader, EmptyState } from "@/components/Badges";

const SelectField = ({ label, value, onChange, options, testId, placeholder }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder || "Choose…"} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-option-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

function AssignDialog({ open, onClose, onSaved }) {
  const { user: me } = useAuth();
  const { regions, communities } = useOrg();
  const [courses, setCourses] = useState([]);
  const [sops, setSops] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ item_type: "course", item_id: "", target_type: "role", target_id: "", due_date: "" });

  useEffect(() => {
    if (!open) return;
    setForm({ item_type: "course", item_id: "", target_type: "role", target_id: "", due_date: "" });
    api.get("/courses").then((r) => setCourses(r.data.filter((c) => c.status === "published")));
    api.get("/sops", { params: { status: "published" } }).then((r) => setSops(r.data));
    api.get("/users", { params: { status: "active" } }).then((r) => setUsers(r.data));
  }, [open]);

  const isRM = me.role === "regional_manager";
  const items = (form.item_type === "course" ? courses : sops).map((x) => ({ value: x.id, label: x.title }));
  const targets = {
    user: users.map((u) => ({ value: u.id, label: `${u.name} · ${u.role_label}` })),
    role: ROLES.filter((r) => r.value !== "admin").map((r) => ({ value: r.value, label: `All ${r.label}s${isRM ? " in my region" : ""}` })),
    community: communities.filter((c) => !isRM || c.region_id === me.region_id).map((c) => ({ value: c.id, label: c.name })),
    region: regions.filter((r) => !isRM || r.id === me.region_id).map((r) => ({ value: r.id, label: r.name })),
  }[form.target_type];

  const item = (form.item_type === "course" ? courses : sops).find((x) => x.id === form.item_id);
  const audience = item?.audience_region_ids || [];
  const audienceNames = regions.filter((r) => audience.includes(r.id)).map((r) => r.name).join(", ");
  const targetRegion = form.target_id && ({
    user: () => users.find((u) => u.id === form.target_id)?.region_id,
    community: () => communities.find((c) => c.id === form.target_id)?.region_id,
    region: () => form.target_id,
    role: () => undefined,
  })[form.target_type]();
  const blocked = audience.length > 0 && form.target_type !== "role" && !!form.target_id && !audience.includes(targetRegion);

  const save = async () => {
    if (blocked) return toast.error("That audience is outside this content's regions");
    if (!form.item_id || !form.target_id) return toast.error("Choose what to assign and who to assign it to");
    try {
      await api.post("/assignments", { ...form, due_date: form.due_date || null });
      toast.success("Assignment created");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="assign-dialog">
        <DialogHeader><DialogTitle className="font-display">New assignment</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-2 rounded-full bg-lasso-paper p-1">
            {[["course", "Course"], ["sop", "SOP acknowledgment"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setForm({ ...form, item_type: v, item_id: "" })} className={`rounded-full py-2 text-xs font-semibold transition-colors ${form.item_type === v ? "bg-lasso-ink text-white" : "text-zinc-600"}`} data-testid={`assign-type-${v}`}>{l}</button>
            ))}
          </div>
          <SelectField label={form.item_type === "course" ? "Course" : "SOP"} value={form.item_id} onChange={(v) => setForm({ ...form, item_id: v })} options={items} testId="assign-item-select" />
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Assign to" value={form.target_type} onChange={(v) => setForm({ ...form, target_type: v, target_id: "" })} testId="assign-target-type-select"
              options={[{ value: "user", label: "A person" }, { value: "role", label: "A role" }, { value: "community", label: "A community" }, { value: "region", label: "A region" }]} />
            <div className="space-y-2">
              <Label>Due date <span className="text-muted-foreground">(optional)</span></Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="assign-due-date-input" />
            </div>
          </div>
          <SelectField label="Audience" value={form.target_id} onChange={(v) => setForm({ ...form, target_id: v })} options={targets} testId="assign-target-select" />
          <p className="text-xs text-muted-foreground">Role, community and region assignments automatically apply to people added to that group later.</p>
          {audience.length > 0 && (
            <p className={`rounded-xl px-3 py-2 text-xs font-semibold ${blocked ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-900"}`} data-testid="assign-audience-warning">
              {blocked ? `Not allowed: this ${form.item_type === "course" ? "course" : "SOP"} is Region-specific (${audienceNames}) and the selected audience is outside it.`
                : `Region-specific (${audienceNames}): only people in ${audience.length > 1 ? "these regions" : "this region"} will get it. Anyone outside, including people added to the group later, is skipped.`}
            </p>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold" data-testid="assign-cancel">Cancel</button>
          <button onClick={save} disabled={blocked} className="rounded-full bg-lasso-ink disabled:opacity-40 px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="assign-save">Assign</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Assignments() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/assignments").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Remove assignment "${a.item_title}" → ${a.target_label}?`)) return;
    try {
      await api.delete(`/assignments/${a.id}`);
      toast.success("Assignment removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Training" title="Assignments" description="Assign courses and SOP acknowledgments to a person, role, community or region."
        actions={<button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="assignments-new-button"><Plus className="h-4 w-4" /> New assignment</button>} />
      {items?.length === 0 ? <EmptyState title="No assignments yet" testId="assignments-empty" /> : (
        <div className="overflow-hidden rounded-2xl border border-lasso-line bg-white">
          <Table data-testid="assignments-table">
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Audience</TableHead><TableHead>People</TableHead><TableHead>Due</TableHead><TableHead>Assigned by</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {items?.map((a) => (
                <TableRow key={a.id} data-testid={`assignment-row-${a.id}`}>
                  <TableCell><span className="inline-flex items-center gap-2 font-semibold">{a.item_type === "course" ? <GraduationCap className="h-4 w-4 text-lasso-deep" /> : <FileCheck2 className="h-4 w-4 text-lasso-deep" />}{a.item_title}</span></TableCell>
                  <TableCell className="text-sm">{a.target_label}</TableCell>
                  <TableCell className="text-sm">{a.people_count}</TableCell>
                  <TableCell className="text-sm">{a.due_date ? formatDate(a.due_date + "T12:00:00") : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{a.assigned_by} · {formatDate(a.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {a.can_delete && <button onClick={() => remove(a)} className="rounded-full border border-lasso-line p-2 hover:border-rose-400 hover:text-rose-600" data-testid={`assignment-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <AssignDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
